import { Button } from 'antd';
import { GateOperationEnum, RelayOperationEnum } from 'common/services/card-access-service/card-access.service.types';
import {
  IGateConfiguration,
  IReaderConfiguration,
  IRelayConfiguration,
} from 'common/services/configuration-service/configuration.service.types';
import type { GateControls } from './use-gate-controls';
import './gate-control-options.scss';

type OptionsProps = {
  gate: IGateConfiguration;
  controls: GateControls;
  nameTag: 'div' | 'h1';
};

type Action = { label: string; key: string; onClick: () => void };

const gateActions = [
  { label: 'Trigger Open', operation: GateOperationEnum.Unlock },
  { label: 'Lock Open', operation: GateOperationEnum.UnlockHold },
  { label: 'Lock Close', operation: GateOperationEnum.Lock },
];

const relayActions = [
  { label: 'Trigger Open', operation: RelayOperationEnum.EnergizeTimeOverride },
  { label: 'Lock Open', operation: RelayOperationEnum.HoldEnergized },
  { label: 'Lock Close', operation: RelayOperationEnum.Deenergized },
];

const getReaderName = (configuration: IReaderConfiguration) =>
  configuration.name || `Reader ${configuration.number} (${configuration.direction})`;

const getRelayName = (relay: IRelayConfiguration) => relay.name || `Relay: ${relay.number}`;

const GateOptionRow = ({
  name,
  nameTag: NameTag,
  actions,
  buttonsState,
}: {
  name: string;
  nameTag: OptionsProps['nameTag'];
  actions: Action[];
  buttonsState: GateControls['buttonsState'];
}) => (
  <div className="gate-options">
    <div className="name">
      <NameTag className="h3">{name}</NameTag>
    </div>
    <div className="actions">
      {actions.map((action) => (
        <Button key={action.key} className={buttonsState[action.key] ? 'btn-green' : 'btn'} onClick={action.onClick}>
          {action.label}
        </Button>
      ))}
    </div>
  </div>
);

export const GateReaderOptions = ({ gate, controls, nameTag }: OptionsProps) => (
  <>
    {gate.readers?.map((reader, index) => {
      const panelNumber = gate.panelNumber!;
      const readerNumber = reader.number;
      return (
        <GateOptionRow
          key={`${panelNumber}:${readerNumber}:reader:${index}`}
          name={getReaderName(reader)}
          nameTag={nameTag}
          buttonsState={controls.buttonsState}
          actions={gateActions.map(({ label, operation }) => ({
            label,
            key: controls.formatGateKey(panelNumber, readerNumber, operation),
            onClick: () => controls.onGateClick(panelNumber, readerNumber, operation),
          }))}
        />
      );
    })}
  </>
);

export const GateRelayOptions = ({ gate, controls, nameTag }: OptionsProps) => (
  <>
    {gate.relays?.map((relay, index) => {
      const panelNumber = gate.panelNumber!;
      const relayNumber = relay.number!;
      return (
        <GateOptionRow
          key={`${panelNumber}:${relayNumber}:relay:${index}`}
          name={getRelayName(relay)}
          nameTag={nameTag}
          buttonsState={controls.buttonsState}
          actions={relayActions.map(({ label, operation }) => ({
            label,
            key: controls.formatRelayKey(panelNumber, relayNumber, operation),
            onClick: () => controls.onRelayClick(panelNumber, relayNumber, operation),
          }))}
        />
      );
    })}
  </>
);

export const GateManagementInstructions = () => (
  <>
    <div>
      <span>Trigger Open:</span> Opens the door once. Also use this button if you have held the door open or closed. The door will time out
      and return to normal operation.
    </div>
    <div>
      <span>Lock Open:</span> Keeps the door open until you click Trigger Open again to return the door to its normal function.
    </div>
    <div>
      <span>Lock Close:</span> Keeps the door closed until you click Trigger Open again to return the door to its normal function.
    </div>
  </>
);
